import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isEmailConfirmed: boolean;
  userType: string | null;
  signUp: (email: string, password: string) => Promise<{ error: any | null; data: any | null }>;
  signIn: (email: string, password: string) => Promise<{ error: any | null; data: any | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any | null; data: any | null }>;
  updatePhoneVerification: (verified: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("AuthProvider mounted");
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('Auth state changed:', event, currentSession?.user?.email);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          setIsEmailConfirmed(!!currentSession.user.email_confirmed_at);
        } else {
          setIsEmailConfirmed(false);
        }
      }
    );

    const checkUserType = async (currentSession: Session | null) => {
      if (!currentSession?.user) {
        setUserType(null);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('id', currentSession.user.id)
          .single();

        setUserType(profile?.user_type || null);
        console.log("User type:", profile?.user_type);
      } catch (error) {
        console.error('Error fetching user type:', error);
        setUserType(null);
      }
    };

    const initializeAuth = async () => {
      try {
        console.log("Initializing auth...");
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log("Current session:", currentSession?.user?.email);
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          setIsEmailConfirmed(!!currentSession.user.email_confirmed_at);
          await checkUserType(currentSession);
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // New function to update phone verification status
  const updatePhoneVerification = async (verified: boolean) => {
    if (!user?.id) return;
    
    try {
      console.log(`Updating phone verification status to: ${verified} for user: ${user.id}`);
      
      const { error } = await supabase
        .from('profiles')
        .update({ phone_verified: verified })
        .eq('id', user.id);
        
      if (error) {
        console.error('Error updating phone verification status:', error);
        toast({
          title: "Failed to update verification status",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
      
      console.log('Phone verification status updated successfully');
      // Refresh profile data in session
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (profile) {
        console.log('Updated profile data:', profile);
      }
      
    } catch (error: any) {
      console.error('Error in updatePhoneVerification:', error);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            user_type: 'doctor',
          },
        },
      });
      
      if (error) {
        toast({
          title: "Registration failed",
          description: error.message,
          variant: "destructive",
        });
        return { error, data: null };
      }
      
      // If user is created, create a profile and doctor record
      if (data.user) {
        try {
          console.log(`Creating profile and doctor records for user: ${data.user.id}`);
          
          // Create profile record
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .upsert({
              id: data.user.id,
              user_type: 'doctor',
              phone_verified: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, { onConflict: 'id' });
            
          if (profileError) {
            console.error('Error creating profile:', profileError);
          } else {
            console.log('Profile created successfully');
          }
          
          // Create doctor record
          const { error: doctorError } = await supabase
            .from('doctors')
            .upsert({
              profile_id: data.user.id,
              specialty: '',
              status: 'pending',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }, { onConflict: 'profile_id' });
            
          if (doctorError) {
            console.error('Error creating doctor record:', doctorError);
          } else {
            console.log(`Doctor record created successfully for user: ${data.user.id}`);
          }
        } catch (createError) {
          console.error('Error in profile/doctor record creation:', createError);
        }
      }
      
      toast({
        title: "Registration successful",
        description: "Please check your email to verify your account.",
      });
      
      return { error: null, data };
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
      return { error, data: null };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log("Signing in with:", email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error("Sign in error:", error);
        toast({
          title: "Login failed",
          description: error.message,
          variant: "destructive",
        });
        return { error, data: null };
      }
      
      console.log("Sign in successful:", data.user?.email);
      toast({
        title: "Login successful",
        description: "Welcome back!",
      });
      
      // Check if profile and doctor record exists, if not, create them
      if (data.user) {
        // First check if profile exists
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, user_type, phone_verified')
          .eq('id', data.user.id)
          .maybeSingle();
            
        if (!profileData) {
          // Profile not found, create profile
          console.log('Profile not found, creating one...');
          await supabase
            .from('profiles')
            .insert({
              id: data.user.id,
              user_type: 'doctor',
              phone_verified: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
        }
        
        // Check if doctor record exists
        const { data: doctorData, error: doctorQueryError } = await supabase
          .from('doctors')
          .select('id')
          .eq('profile_id', data.user.id)
          .maybeSingle();
          
        if (!doctorData) {
          // Doctor record not found, create it
          console.log('Doctor record not found, creating one...');
          
          await supabase
            .from('doctors')
            .insert({
              profile_id: data.user.id,
              specialty: '',
              status: 'pending',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          
          console.log('Doctor record created during login');
        }
      }
      
      return { error: null, data };
    } catch (error: any) {
      console.error("Sign in exception:", error);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
      return { error, data: null };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setIsEmailConfirmed(false);
      navigate('/auth/doctor/login');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      if (error) {
        toast({
          title: "Password reset failed",
          description: error.message,
          variant: "destructive",
        });
        return { error, data: null };
      }
      
      toast({
        title: "Password reset email sent",
        description: "Please check your email to reset your password.",
      });
      
      return { error: null, data };
    } catch (error: any) {
      toast({
        title: "Password reset failed",
        description: error.message,
        variant: "destructive",
      });
      return { error, data: null };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    session,
    user,
    loading,
    isAuthenticated: !!session,
    signUp,
    signIn,
    signOut,
    resetPassword,
    isEmailConfirmed,
    userType,
    updatePhoneVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
